import { useState, useEffect, useCallback } from 'react';
import { Loader2, Search, Filter, ChevronDown, ChevronUp, Save, MessageSquare, Clock, User, Globe, ExternalLink, Paperclip, Download, FilePlus } from "lucide-react";
import ticketService from '../../../services/ticketService';
import websiteService from '../../../services/websiteService';
import userService from '../../../services/userService';
import Toast from "../../../components/Toast";
import { formatDistanceToNow, format } from "date-fns";

const Tickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalTickets, setTotalTickets] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [filters, setFilters] = useState({
    status: "",
    user_id: "",
    searchTerm: "",
    sortBy: "created_at",
    sortOrder: "desc"
  });
  const [showFilters, setShowFilters] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);
  const [creditForm, setCreditForm] = useState({
    support_credits: "",
    custom_credits: "",
    credit_confirmed: false
  });
  const [toast, setToast] = useState({ open: false, message: '', severity: 'error' });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetails, setTicketDetails] = useState(null);
  const [websiteDetails, setWebsiteDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [userDetails, setUserDetails] = useState(null);

  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      const offset = (currentPage - 1) * itemsPerPage;
      
      const response = await ticketService.getAllTicketsAdmin({
        limit: itemsPerPage,
        offset,
        status: filters.status || undefined,
        user_id: filters.user_id || undefined,
        searchTerm: filters.searchTerm || undefined,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder
      });

      if (response && response.success) {
        setTickets(response.tickets || []);
        setTotalTickets(response.total || 0);
      }
    } catch (error) {
      console.error("Error fetching tickets:", error);
      setToast({
        open: true,
        message: "Failed to load tickets. Please try again later.",
        severity: "error"
      });
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, filters]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleSortChange = (field) => {
    setFilters(prev => ({
      ...prev,
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleCreditFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCreditForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleEditTicket = (ticket) => {
    setEditingTicket(ticket);
    setCreditForm({
      support_credits: ticket.support_credits || "",
      custom_credits: ticket.custom_credits || "",
      credit_confirmed: ticket.credit_confirmed || false
    });
  };

  const handleUpdateCredits = async (e) => {
    e.preventDefault();
    if (!editingTicket) return;

    try {
      setLoading(true);
      const response = await ticketService.updateTicketCreditCost(editingTicket.id, {
        support_credits: creditForm.support_credits ? parseInt(creditForm.support_credits) : null,
        custom_credits: creditForm.custom_credits ? parseInt(creditForm.custom_credits) : null,
        credit_confirmed: creditForm.credit_confirmed
      });

      if (response && response.success) {
        setToast({
          open: true,
          message: "Ticket credits updated successfully",
          severity: "success"
        });
        setEditingTicket(null);
        fetchTickets(); // Refresh the tickets list
      }
    } catch (error) {
      console.error("Error updating ticket credits:", error);
      setToast({
        open: true,
        message: "Failed to update ticket credits. Please try again.",
        severity: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'open':
        return 'bg-blue-100 text-blue-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleTicketClick = async (ticket) => {
    setSelectedTicket(ticket);
    setLoadingDetails(true);
    setUserDetails(null);
    try {
      // Fetch ticket details
      const ticketResponse = await ticketService.getTicketById(ticket.id);
      if (ticketResponse && ticketResponse.success) {
        setTicketDetails(ticketResponse.ticket);
        
        // Fetch website details if website_id exists
        if (ticketResponse.ticket.website_id) {
          try {
            const websiteResponse = await websiteService.getWebsiteById(ticketResponse.ticket.website_id);
            if (websiteResponse && websiteResponse.success) {
              // Check if website data is a string and parse it if needed
              let websiteData = websiteResponse;
              console.log("Website data:", websiteData);
              if (typeof websiteData === 'string') {
                try {
                  websiteData = JSON.parse(websiteData);
                } catch (err) {
                  console.error("Error parsing website data:", err);
                }
              }
              setWebsiteDetails(websiteData.website);
            }
          } catch (error) {
            console.error("Error fetching website details:", error);
          }
        }
        // Fetch user details
        if (ticketResponse.ticket.user_id) {
          try {
            const userResponse = await userService.getUserById(ticketResponse.ticket.user_id);
            
            if (userResponse && userResponse.success) {
              setUserDetails(userResponse.data);
            }
          } catch (error) {
            console.error("Error fetching user details:", error);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching ticket details:", error);
      setToast({
        open: true,
        message: "Failed to load ticket details",
        severity: "error"
      });
    } finally {
      setLoadingDetails(false);
    }
  };

  // Format conversations from the response data
  const formatConversations = (ticket) => {
    if (!ticket) return [];
    
    let freescoutData = ticket.freescout_data;
    if (typeof freescoutData === 'string') {
      try {
        freescoutData = JSON.parse(freescoutData);
      } catch (error) {
        console.error("Error parsing freescout_data:", error);
        return [];
      }
    }
    
    if (!freescoutData?._embedded?.threads) {
      return [];
    }
    
    const threads = freescoutData._embedded.threads.map(thread => {
      const isLineItem = thread.type === 'lineitem';
      const isCustomer = thread.type === 'customer';
      const isUser = thread.type === 'message';
      const isNote = thread.type === 'note';
      const isStatusUpdate = isLineItem && thread.action && thread.action.type === 'changed-ticket-status';
      
      const attachments = thread._embedded?.attachments || [];
      const createdDate = thread.createdAt ? new Date(thread.createdAt).toISOString() : null;
      
      let userName = 'Support';
      if (isCustomer) {
        userName = `${thread.customer?.firstName || ''} ${thread.customer?.lastName || ''}`.trim();
      } else if ((isUser || isNote) && thread.createdBy) {
        userName = `${thread.createdBy?.firstName || ''} ${thread.createdBy?.lastName || ''}`.trim();
        if (!userName) {
          userName = 'Support';
        }
      }
      
      return {
        id: thread.id,
        message: isNote ? '[Sensitive information redacted]' : isLineItem ? (thread.action?.text || 'Support update') : thread.body,
        created_at: createdDate,
        updated_at: createdDate,
        user_name: userName,
        isCustomer,
        isUser,
        isNote,
        isLineItem,
        isStatusUpdate,
        action: thread.action,
        status: thread.status,
        statusText: isStatusUpdate ? thread.action.text : null,
        synced_with_freescout: true,
        attachments: attachments.map(att => ({
          id: att.id,
          name: att.fileName || att.filename,
          size: att.size || 0,
          url: att.fileUrl || att.url,
          mime_type: att.mimeType || att.content_type
        }))
      };
    });
    
    return threads.sort((a, b) => {
      if (!a.created_at) return 1;
      if (!b.created_at) return -1;
      return new Date(b.created_at) - new Date(a.created_at);
    });
  };

  const formatMessageDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return format(date, "MMM d, yyyy 'at' h:mm a");
    } catch (err) {
      return "Invalid date";
    }
  };

  return (
    <div className="container p-4 mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Tickets Management</h1>
        <p className="text-gray-500">Manage and update ticket credit costs</p>
      </div>

      {/* Filters */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 mb-6">
        <div 
          className="px-6 py-4 border-b border-gray-200 flex justify-between items-center cursor-pointer"
          onClick={() => setShowFilters(!showFilters)}
        >
          <div className="flex items-center">
            <Filter className="h-5 w-5 mr-2" />
            <h2 className="text-lg font-medium">Filters</h2>
          </div>
          <button className="text-gray-500">
            {showFilters ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </button>
        </div>
        
        {showFilters && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                id="status"
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
              >
                <option value="">All Statuses</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div>
              <label htmlFor="user_id" className="block text-sm font-medium text-gray-700 mb-1">User ID</label>
              <input
                type="text"
                id="user_id"
                name="user_id"
                value={filters.user_id}
                onChange={handleFilterChange}
                placeholder="Filter by user ID"
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="searchTerm" className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <div className="relative">
                <input
                  type="text"
                  id="searchTerm"
                  name="searchTerm"
                  value={filters.searchTerm}
                  onChange={handleFilterChange}
                  placeholder="Search by subject, description, or ticket ID"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 pl-10"
                />
                <Search className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tickets Table */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSortChange('id')}
                >
                  ID {filters.sortBy === 'id' && (filters.sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSortChange('user_id')}
                >
                  User {filters.sortBy === 'user_id' && (filters.sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSortChange('subject')}
                >
                  Subject {filters.sortBy === 'subject' && (filters.sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSortChange('status')}
                >
                  Status {filters.sortBy === 'status' && (filters.sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSortChange('created_at')}
                >
                  Created {filters.sortBy === 'created_at' && (filters.sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSortChange('support_credits')}
                >
                  Support Credits {filters.sortBy === 'support_credits' && (filters.sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSortChange('custom_credits')}
                >
                  Custom Credits {filters.sortBy === 'custom_credits' && (filters.sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSortChange('credit_confirmed')}
                >
                  Confirmed {filters.sortBy === 'credit_confirmed' && (filters.sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="9" className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center">
                      <Loader2 className="h-6 w-6 animate-spin text-blue-500 mr-2" />
                      <span className="text-gray-600">Loading tickets...</span>
                    </div>
                  </td>
                </tr>
              ) : tickets.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-4 text-center text-gray-500">
                    No tickets found
                  </td>
                </tr>
              ) : (
                tickets.map((ticket) => (
                  <tr 
                    key={ticket.id}
                    onClick={() => handleTicketClick(ticket)}
                    className="cursor-pointer hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {ticket.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {ticket.user_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {ticket.subject}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(ticket.status)}`}>
                        {ticket.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(ticket.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {ticket.support_credits || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {ticket.custom_credits || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        ticket.credit_confirmed ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {ticket.credit_confirmed ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation(); // Stop event from propagating to parent row
                          handleEditTicket(ticket);
                        }}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Edit Credits
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalTickets > itemsPerPage && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalTickets)} of {totalTickets} tickets
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={currentPage * itemsPerPage >= totalTickets}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Credits Modal */}
      {editingTicket && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium">Update Ticket Credits</h3>
              <p className="text-sm text-gray-500">Ticket ID: {editingTicket.id}</p>
            </div>
            <form onSubmit={handleUpdateCredits} className="p-6">
              <div className="space-y-4">
                <div>
                  <label htmlFor="support_credits" className="block text-sm font-medium text-gray-700 mb-1">
                    Support Credits
                  </label>
                  <input
                    type="number"
                    id="support_credits"
                    name="support_credits"
                    value={creditForm.support_credits}
                    onChange={handleCreditFormChange}
                    min="0"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                  />
                </div>
                <div>
                  <label htmlFor="custom_credits" className="block text-sm font-medium text-gray-700 mb-1">
                    Custom Development Credits
                  </label>
                  <input
                    type="number"
                    id="custom_credits"
                    name="custom_credits"
                    value={creditForm.custom_credits}
                    onChange={handleCreditFormChange}
                    min="0"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="credit_confirmed"
                    name="credit_confirmed"
                    checked={creditForm.credit_confirmed}
                    onChange={handleCreditFormChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="credit_confirmed" className="ml-2 block text-sm text-gray-900">
                    Confirm credit allocation
                  </label>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setEditingTicket(null)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="animate-spin h-4 w-4 mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Ticket Details Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-medium">Ticket Details</h3>
              <button
                onClick={() => {
                  setSelectedTicket(null);
                  setTicketDetails(null);
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {loadingDetails ? (
              <div className="p-6 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              </div>
            ) : ticketDetails ? (
              <div className="p-6">
                {/* Ticket Overview */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
                    <div className="flex items-start">
                      <div>
                        <h1 className="text-2xl font-bold">{ticketDetails.subject}</h1>
                        <div className="mt-2 flex flex-wrap gap-2 items-center">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(ticketDetails.status)}`}>
                            {ticketDetails.status}
                          </span>
                          <span className="text-gray-500 text-sm flex items-center">
                            <Clock className="h-4 w-4 mr-1" />
                            {formatDistanceToNow(new Date(ticketDetails.created_at), { addSuffix: true })}
                          </span>
                          {!ticketDetails.website_id && (
                            <span className="text-gray-500 text-sm flex items-center">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              Email Ticket
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-4 mt-4">
                    {/* User Information */}
                    <div className="mb-4">
                      <h3 className="font-medium text-gray-700 flex items-center mb-2">
                        <User className="h-4 w-4 mr-1" />
                        User Information
                      </h3>
                      <div className="text-sm space-y-2">
                        {userDetails ? (
                          <>
                            <div className="flex items-center">
                              <span className="font-medium text-gray-700 w-24">Name:</span>
                              <span className="text-gray-600">{userDetails.name}</span>
                            </div>
                            <div className="flex items-center">
                              <span className="font-medium text-gray-700 w-24">Email:</span>
                              <a 
                                href={`mailto:${userDetails.email}`}
                                className="text-blue-500 hover:text-blue-700"
                              >
                                {userDetails.email}
                              </a>
                            </div>
                            <div className="flex items-center">
                              <span className="font-medium text-gray-700 w-24">ID:</span>
                              <span className="text-gray-600">{userDetails.id}</span>
                            </div>
                          </>
                        ) : (
                          <div className="flex items-center">
                            <span className="font-medium text-gray-700 w-24">ID:</span>
                            <span className="text-gray-600">{ticketDetails.user_id}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Ticket Details */}
                    <div className="mb-4">
                      <h3 className="font-medium text-gray-700 flex items-center mb-2">
                        <Clock className="h-4 w-4 mr-1" />
                        Ticket Details
                      </h3>
                      <div className="text-sm space-y-2">
                        <div className="flex items-center">
                          <span className="font-medium text-gray-700 w-24">Created:</span>
                          <span className="text-gray-600">{formatMessageDate(ticketDetails.created_at)}</span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium text-gray-700 w-24">Support Credits:</span>
                          <span className="text-gray-600">{ticketDetails.support_credits || '-'}</span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium text-gray-700 w-24">Custom Credits:</span>
                          <span className="text-gray-600">{ticketDetails.custom_credits || '-'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Website Information */}
                    {ticketDetails.website_id ? (
                      <div>
                        <h3 className="font-medium text-gray-700 flex items-center mb-2">
                          <Globe className="h-4 w-4 mr-1" />
                          Website Information
                        </h3>
                        <div className="text-sm space-y-2">
                          {websiteDetails && (websiteDetails.name || websiteDetails.root_url) ? (
                            <>
                              {websiteDetails.name && (
                                <div className="flex items-center">
                                  <span className="font-medium text-gray-700 w-24">Name:</span>
                                  <span className="text-gray-600">{websiteDetails.name}</span>
                                </div>
                              )}
                              {websiteDetails.root_url && (
                                <div className="flex items-center">
                                  <span className="font-medium text-gray-700 w-24">URL:</span>
                                  <a 
                                    href={websiteDetails.root_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-blue-500 hover:text-blue-700 inline-flex items-center"
                                  >
                                    {websiteDetails.root_url}
                                    <ExternalLink className="h-3 w-3 ml-1" />
                                  </a>
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="flex items-center">
                              <span className="font-medium text-gray-700 w-24">ID:</span>
                              <span className="text-gray-600">{ticketDetails.website_id}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                        <div className="flex items-center text-gray-600">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <div>
                            <h3 className="font-medium text-gray-700">Email Ticket</h3>
                            <p className="text-sm text-gray-500">This ticket was created via email and is not associated with any website.</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Conversation History */}
                <div className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-semibold flex items-center">
                      <MessageSquare className="h-5 w-5 mr-2" />
                      Conversation History
                    </h2>
                  </div>

                  <div className="space-y-6">
                    {formatConversations(ticketDetails).map((message, index) => (
                      <div
                        key={message.id || index}
                        className="relative z-8 ml-9"
                      >
                        <div className={`p-4 rounded-lg ${
                          message.isStatusUpdate
                            ? "bg-gray-50 border border-gray-200"
                            : message.isCustomer
                              ? "bg-blue-50 border border-blue-200"
                              : "bg-green-50 border border-green-200"
                        }`}>
                          <div className="flex justify-between mb-3">
                            <div className="flex items-center">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-2 ${
                                message.isCustomer 
                                  ? 'bg-blue-100 text-blue-600' 
                                  : 'bg-green-100 text-green-600'
                              }`}>
                                {message.isCustomer ? (
                                  <User className="h-4 w-4" />
                                ) : (
                                  message.user_name ? message.user_name.charAt(0).toUpperCase() : 'S'
                                )}
                              </div>
                              <span className="font-medium">
                                {message.isCustomer ? "Customer" : message.user_name || "Support Agent"}
                                <span className="text-xs text-gray-500 ml-2">
                                  {formatMessageDate(message.created_at).split(',')[1]}
                                </span>
                              </span>
                            </div>
                          </div>
                          <div
                            className="text-gray-700 whitespace-pre-wrap break-words prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: message.message }}
                          />

                          {/* Attachments */}
                          {message.attachments && message.attachments.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                              <div className="text-sm font-medium text-gray-500 mb-2 flex items-center">
                                <Paperclip className="h-4 w-4 mr-1" />
                                Attachments:
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {message.attachments.map((attachment) => (
                                  <div
                                    key={attachment.id}
                                    className="flex items-center bg-gray-50 rounded-md p-2 border border-gray-200"
                                  >
                                    <div className="h-8 w-8 flex-shrink-0 rounded bg-gray-200 flex items-center justify-center mr-2">
                                      {attachment.mime_type?.includes('image') ? (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                          <circle cx="8.5" cy="8.5" r="1.5" />
                                          <polyline points="21 15 16 10 5 21" />
                                        </svg>
                                      ) : (
                                        <FilePlus className="h-4 w-4 text-gray-500" />
                                      )}
                                    </div>
                                    <div className="flex-1 truncate">
                                      <span className="text-sm font-medium">
                                        {attachment.name}
                                      </span>
                                      <span className="text-xs text-gray-500 block">
                                        {(attachment.size / 1024).toFixed(1)} KB
                                      </span>
                                    </div>
                                    <a
                                      href={attachment.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="ml-2 text-blue-500 hover:text-blue-700 p-1 hover:bg-blue-50 rounded-full transition-colors"
                                      title="Download file"
                                    >
                                      <Download className="h-4 w-4" />
                                    </a>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-6 text-center text-gray-500">
                Failed to load ticket details
              </div>
            )}
          </div>
        </div>
      )}

      {/* Toast notifications */}
      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast({ ...toast, open: false })}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      />
    </div>
  );
};

export default Tickets; 